import videojs from 'video.js';
import 'videojs-overlay';
// Import CSS files as raw strings to get access to css text
import videojsCSS from 'video.js/dist/video-js.css?raw';
import componentCSS from './component-style.css?raw';
// Import template as raw HTML string
import templateHTML from './template.html?raw';
// Import font file as a bundled URL - Vite will include it in the build
import videoJsFontUrl from 'video.js/dist/font/VideoJS.woff?url';
import ciscoSansRegularUrl from '../assets/fonts/CiscoSans/CiscoSansTTRegular.woff2?url';
import ciscoCuiFontUrl from '../assets/fonts/CiscoSans/cui-font.woff2?url';
// Import icons as bundled URLs — needed because CSS is loaded as a raw strings in Shadow DOM
import iconTranscript from '../assets/icons/transcript.svg?url';
import iconChapters  from '../assets/icons/chapters.svg?url';
import iconSummary   from '../assets/icons/summary.svg?url';
import iconCollapse  from '../assets/icons/collapse.svg?url';
import iconExpand    from '../assets/icons/expand.svg?url';
import iconLike     from '../assets/icons/upvote.png?url';
import iconDislike  from '../assets/icons/downvote.png?url';
import iconLiked   from '../assets/icons/upvoted.png?url';
import iconDisliked from '../assets/icons/downvoted.png?url';
import iconDownload from '../assets/icons/sprite-img.png?url';
import iconGlobe from '../assets/icons/globe_black.svg?url';
import iconInfoDark  from '../assets/icons/info-icon-dark.png?url';
import iconInfoLight from '../assets/icons/info-icon-light.png?url';
// video.js locale JSON files — bundled statically so no CDN/runtime fetch needed
import langEn   from 'video.js/dist/lang/en.json';
import langZhCN from 'video.js/dist/lang/zh-CN.json';
import langJa   from 'video.js/dist/lang/ja.json';
import langPtPT from 'video.js/dist/lang/pt-PT.json';
import langKo   from 'video.js/dist/lang/ko.json';
import langEs   from 'video.js/dist/lang/es.json';
import langFr   from 'video.js/dist/lang/fr.json';
import { getTimestamp, deviceType, browserType, secondsTimeSpanToHMS } from './audit-helpers.js';
import { ConfigManager } from './config.js';
import { t } from './i18n.js';

class CdamVideoPlayer extends HTMLElement {
  // Static cache shared across ALL instances
  static cachedStyleSheet = null;
  static cachedTemplateContent = null;
  
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });

    // Audit tracking state
    this.trackingObj = {};
    this.firstPlay   = true;

    // Resolved in connectedCallback / SSO flow
    this.config          = null;
    this.assetId         = '';
    this.videoUrl        = '';

    // SSO auth state
    this.isAuthenticated = false;
    this.accessToken     = ''; 
    this.userId          = '';  // set from user-id attribute in connectedCallback

    // Audit metadata
    this.tenant      = 'POC';
    this.sessionType = 'Success Tip';

    // Progress tracking
    this.playcount       = 0;
    this.pauseSeeking    = false;
    this.clientParam     = 'undefined';
    this.expParam        = 'undefined';

    // Transcript state
    this.captions     = [];  // [{begin, end, btext, text}] parsed from VTT
    this.captionIndex = -1;  // index of currently highlighted caption
    this.vttUrl       = '';
    this.vttFound     = false;

    // Chapters state
    this.chapters     = [];
    this.chapterIndex = -1;

    // Overlay state
    this.overlayLinks = [];

    // Feedback state
    this.feedbackObj  = { _liked: null };
    this.video_source_language = '';
    this.languages = [
      {'label': 'EN US', value: 'en'},
      {'label': 'Chinese (中文)', value: 'zh-CN'},
      {'label': 'Japanese (日本語)', value: 'ja'},
      {'label': 'Portuguese (Português)', value: 'pt-PT'},
      {'label': 'Korean (한국어 )', value: 'ko'},
      {'label': 'Spanish (Español)', value: 'es'},
      {'label': 'French (Français)', value: 'fr'}
		];
  }

  async connectedCallback() {
    const hostName = window.location.hostname;
    const env = hostName === 'localhost'
      ? 'localhost'
      : hostName === 'kxiwq67737.preprod.lithium.com'
        ? 'stage'
        : 'prod';
    this.config  = ConfigManager.retrieveConfig(env);

    // Read asset ID and user identity from custom element attributes
    this.assetId     = this.getAttribute('asset-id')       || '';
    this.userId      = this.getAttribute('user-id')         || '';  // '' when not authenticated
    this.expParam    = this.getAttribute('experience-id')   || 'undefined';

    // Normalise client-id: 'CX-Community' to 'CX'
    const rawClientId    = this.getAttribute('client-id')  || 'undefined';
    this.clientParam     = rawClientId === 'CX-Community' ? 'CX' : rawClientId;

    // Construct full video URL from config
    this.videoUrl = this.config.video_base_url + this.assetId
                  + '&client=' + this.clientParam
                  + '&exp='    + this.expParam;

    // Transcript VTT endpoint
    this.vttUrl = this.config.vtt_base_url + this.assetId;
    this.chapterUrl = this.config.chapter_url + '/chapters?id=' + this.assetId;
    this.summaryUrl = this.config.chapter_url + '/summary?id=' + this.assetId;  

    this.loadStyles();
    this.loadTemplate();
    this.setupSidebarTabs();
    this.setupSearch();
    this.initializeVideoPlayer();
    this.setupTranscript();       // register loadstart handler BEFORE src is set
    this.getAssetDetails();    // async fetch for video language metadata used in feedback → doesn't need to block player load
    this.loadPlayer(null);        // sets src → triggers loadstart → VTT fetch begins
    this.setupAuditTracking();
    await this.initSSO();         // auth flow in background → seeks to resume position if authenticated
    this.render();
  }


  loadStyles() {
    // If already cached, use it
    if (CdamVideoPlayer.cachedStyleSheet) {
      this.applyStyles();
      return;
    }
    
    // First instance: process and cache styles
    this.initializeStyles();
    this.applyStyles();
  }

  
  initializeStyles() {
    // Extract @font-face rules - must be in global scope
    const fontFaceRegex = /@font-face\s*\{[^}]+\}/gs;
    const fontFaces = videojsCSS.match(fontFaceRegex) || [];
    const videojsCssWithoutFonts = videojsCSS.replace(fontFaceRegex, '');
    
    // Replace data: URLs with bundled font URL (imported via Vite)
    // To avoids slow CDN requests AND CSP violations from data: URLs
    const secureFontFaces = fontFaces.map(fontFace => {
      return fontFace.replace(
        /url\(data:application\/font-woff[^)]+\)/g,
        `url(${videoJsFontUrl})`  // Vite bundles this locally
      );
    });
    
    // Add secure @font-face to global scope (only once)
    if (secureFontFaces.length > 0 && !document.getElementById('videojs-font-global')) {
      const globalFontStyle = document.createElement('style');
      globalFontStyle.id = 'videojs-font-global';
      globalFontStyle.textContent = secureFontFaces.join('\n');
      document.head.appendChild(globalFontStyle);
    }


    // Add CUI icon font @font-face to global scope -- icon fonts need global registration
    // to render reliably in Shadow DOM across all browsers.
    if (!document.getElementById('cui-font-global')) {
      const cuiFontStyle = document.createElement('style');
      cuiFontStyle.id = 'cui-font-global';
      cuiFontStyle.textContent = `@font-face { font-family: 'cdam-cui-font'; src: url(${ciscoCuiFontUrl}) format('woff2'); }`;
      document.head.appendChild(cuiFontStyle);
    }
    const ciscoSansCSS = `
      @font-face {
          font-family: 'CiscoSans';
          src: url(${ciscoSansRegularUrl}) format('woff2');
          font-style: normal;
          font-weight: 400;
          font-display: swap;
      }
      @font-face {
        font-family: 'cdam-cui-font';
        src: url(${ciscoCuiFontUrl}) format('woff2');
      }
      [class^="icon-"] {
        position: relative;
        display: inline-block;
        font-family: 'cdam-cui-font';
        font-size: inherit;
        font-style: normal;
        font-weight: normal;
        speak: none;
        text-decoration: inherit;
        text-transform: none;
        text-rendering: optimizeLegibility;
        vertical-align: middle;
      }
    `;

    // Create shared CSSStyleSheet object
    const combinedCSS = videojsCssWithoutFonts + '\n\n' + componentCSS + '\n\n' + ciscoSansCSS;
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(combinedCSS); // Sync version since CSS is already loaded
    CdamVideoPlayer.cachedStyleSheet = sheet; // Cache for all instances
  }
  
  applyStyles() {
    // Apply shared stylesheet to this instance's shadow root
    this.shadowRoot.adoptedStyleSheets = [CdamVideoPlayer.cachedStyleSheet];
  }

  loadTemplate() {
    // If already cached, use it
    if (CdamVideoPlayer.cachedTemplateContent) {
      this.applyTemplate();
      return;
    }
    
    // First instance: parse and cache template
    this.initializeTemplate();
    this.applyTemplate();
  }
  
  initializeTemplate() {
    // Parse template from inlined HTML string
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = templateHTML;
    const template = tempDiv.querySelector('template');
    
    // Cache the template content for cloning
    CdamVideoPlayer.cachedTemplateContent = template.content;
  }
  
  applyTemplate() {
    // Clone cached template for this instance
    const content = CdamVideoPlayer.cachedTemplateContent.cloneNode(true);
    // Append to shadow root
    this.shadowRoot.appendChild(content);
    this.shadowRoot.querySelector('.chapters-icon') .src = iconChapters;
    this.shadowRoot.querySelector('.transcript-icon').src = iconTranscript;
    this.shadowRoot.querySelector('.summary-icon')  .src = iconSummary;
    // Sidebar starts expanded — showing collapse icon
    this.shadowRoot.querySelector('#collapse-icon') .src = iconCollapse;
    this.shadowRoot.querySelector('.sprite-img').style.backgroundImage = `url(${iconDownload})`;
    // feedback icons
    const likeImg = this.shadowRoot.querySelector('.video-like-img');
    if (likeImg) likeImg.style.backgroundImage = `url(${iconLike})`;
    const dislikeImg = this.shadowRoot.querySelector('.video-dislike-img');
    if (dislikeImg) dislikeImg.style.backgroundImage = `url(${iconDislike})`;
    const likedImg = this.shadowRoot.querySelector('.video-liked-img');
    if (likedImg) likedImg.style.backgroundImage = `url(${iconLiked})`;
    const dislikedImg = this.shadowRoot.querySelector('.video-disliked-img');
    if (dislikedImg) dislikedImg.style.backgroundImage = `url(${iconDisliked})`;
  }

  initializeVideoPlayer() {
    // Register video.js locale data from bundled JSON files
    this.registerLanguages();

    // Initialize video.js player - Shadow DOM scoping ensures we only find this instance's video
    const videoElement = this.shadowRoot.querySelector('#vidMain');

    if (videoElement) {
      // videojs is now available from import
      this.player = videojs(videoElement, {
        controls: true,
        autoplay: false,
        muted: false,
        preload: 'auto',
        fluid: true,
        responsive: true,
        playbackRates: [0.5, 1, 1.25, 1.5, 1.75, 2],
        controlBar: {
          volumePanel: {
            inline: false
          },
          remainingTimeDisplay: false,
          currentTimeDisplay: true,
          timeDivider: true,
          durationDisplay: true
        },
        language: this.video_source_language || 'en'  // default to English until we fetch the actual language from metadata
      });

      // src is NOT set here — loadPlayer() sets it after SSO resolves

      // Apply initial translations and re-apply whenever the language changes
      this.applyCustomTranslations();
      this.player.on('languagechange', () => this.applyCustomTranslations());
    } else {
      console.error('Video element not found');
    }
  }

  /**
   * Translate every [data-i18n] text node and [data-i18n-title] attribute
   * in the shadow DOM to the player's current language.
   */
  applyCustomTranslations() {
    const locale = this.player ? this.player.language() : 'en';
    const root   = this.shadowRoot;

    // Text content
    root.querySelectorAll('[data-i18n]').forEach(el => {
      el.textContent = t(el.getAttribute('data-i18n'), locale);
    });

    // title / aria-label attributes
    root.querySelectorAll('[data-i18n-title]').forEach(el => {
      el.title = t(el.getAttribute('data-i18n-title'), locale);
    });

    // Update seek button tooltips in the video.js control bar
    if (this.player && this.player.controlBar) {
      const fwd = this.player.controlBar.getChild('SeekForwardButton');
      const bwd = this.player.controlBar.getChild('SeekBackwardButton');
      const langButton = this.player.controlBar.getChild('LanguageMenuButton');
      if (fwd) fwd.controlText(t('Seek Forward 15 seconds', locale));
      if (bwd) bwd.controlText(t('Seek Backward 15 seconds', locale));
      if (langButton) langButton.controlText(t('Language', locale));
    }
  }

  registerLanguages() {
    // Register all supported locales with video.js once (idempotent)
    videojs.addLanguage('en',    langEn);
    videojs.addLanguage('zh-CN', langZhCN);
    videojs.addLanguage('ja',    langJa);
    videojs.addLanguage('pt-PT', langPtPT);
    videojs.addLanguage('ko',    langKo);
    videojs.addLanguage('es',    langEs);
    videojs.addLanguage('fr',    langFr);
  }

  setupSidebarTabs() {
    const root = this.shadowRoot;

    // Show the sidebar panel
    root.querySelector('#playlist').style.display = 'flex';

    const activateTab = (targetId) => {
      root.querySelectorAll('.custom-transcript').forEach(el => el.style.display = 'none');
      root.querySelector('#' + targetId).style.display = 'block';
      root.querySelectorAll('#side-menu-items .nav-link').forEach(li => li.classList.remove('active-item'));
      root.querySelector(`[data-target="${targetId}"]`).classList.add('active-item');
    };

    activateTab('chaptersDiv');

    // Collapse / expand toggle
    const toggleBtn        = root.querySelector('#collapse-icon');
    const toggleStrip      = root.querySelector('.collapse-transcript');
    const playlist         = root.querySelector('#playlist');
    const videoWindow      = root.querySelector('#video_window');
    const transcriptWindow = root.querySelector('#transcript_window');
    const sideMenuItems    = root.querySelector('#side-menu-items');

    let sidebarExpanded = true;

    const setExpandView = () => {
      sidebarExpanded = true;
      toggleBtn.src = iconCollapse;
      playlist.style.display = 'flex';
      videoWindow.classList.remove('m-left-9');
      transcriptWindow.classList.add('col-md-4');
      transcriptWindow.classList.remove('col');
      transcriptWindow.style.removeProperty('max-width');
      sideMenuItems.style.borderLeft = '1px solid #8E8E8E';
    };

    const setCollapseView = () => {
      sidebarExpanded = false;
      toggleBtn.src = iconExpand;
      playlist.style.display = 'none';
      videoWindow.classList.add('m-left-9');
      transcriptWindow.classList.remove('col-md-4');
      transcriptWindow.classList.add('col');
      transcriptWindow.style.maxWidth = '144px';
      sideMenuItems.style.borderLeft = 'none';
    };

    toggleStrip.addEventListener('click', () => {
      if (sidebarExpanded) {
        setCollapseView();
      } else {
        setExpandView();
      }
    });

    root.querySelectorAll('#side-menu-items .nav-link').forEach(li => {
      li.addEventListener('click', () => {
        setExpandView();                                         // auto-expand if collapsed
        const targetId = li.getAttribute('data-target');
        activateTab(targetId);
        if (targetId === 'summary') {
          this.saveSummaryData();
        }
      });
    });
  }

  setupTranscript() {
    this.player.on('loadstart', () => {
      // Reset transcript state for each new video load
      this.captions     = [];
      this.captionIndex = -1;
      this.vttFound     = false;

      // Reset chapters state for each new video load
      this.chapters     = [];
      this.chapterIndex = -1;

      if (!this.vttUrl) return;

      const fetchUrl = this.vttUrl;
      fetch(fetchUrl)
        .then(response => {
          if (!response.ok) throw new Error(`VTT fetch failed: HTTP ${response.status}`);
          return response.text();
        })
        .then(text => {
          this.loadCaptions(text);
          this.vttFound = true;
          this.trackingObj['transcript_available'] = true;
        })
        .catch(err => {
          console.warn('Transcript not available:', err.message);
          this.vttFound = false;
          this.trackingObj['transcript_available'] = false;
          this._showEmptyTranscript();
        });
        if (this.player.controlBar.subsCapsButton) {
          this.player.controlBar.subsCapsButton.hide();
        }
        this.closedCaptionButton();
        this.seekButtons();
        this.setupLanguageIcon();
        this.player.controlBar.addChild('YouTubeCCButton', {}, this.player.controlBar.children().length - 2);
        this.player.controlBar.addChild('SeekBackwardButton', {}, 1);
        this.player.controlBar.addChild('SeekForwardButton', {}, 2);
        const children = this.player.controlBar.children();

        // Find index of playback rate button
        const playbackIndex = children.findIndex(
          child => child.name && child.name() === 'PlaybackRateMenuButton'
        );
        this.player.controlBar.addChild('LanguageMenuButton', {}, playbackIndex);
        this.setupChapters();
        this.setupSummary();
        this.setupFeedbackButton();
        this.downloadTranscript();
   });
  }

  loadTextTracks() {
    // Remove any existing remote text tracks to avoid duplicates on re-load
    const existing = this.player.remoteTextTracks();
    for (let i = existing.length - 1; i >= 0; i--) {
      this.player.removeRemoteTextTrack(existing[i]);
    }

    const sourceLang = (this.video_source_language || 'en').toLowerCase();
    // Put source language first so it becomes the default (index-0) track
    const sortedLanguages = [
      ...this.languages.filter(l => l.value.toLowerCase() === sourceLang),
      ...this.languages.filter(l => l.value.toLowerCase() !== sourceLang)
    ];
    sortedLanguages.forEach(lang => {
      const isSource = lang.value.toLowerCase() === sourceLang;
      this.player.addRemoteTextTrack({
        kind: 'captions',
        label: lang.label,
        srclang: lang.value,
        src: isSource ? this.vttUrl : `${this.vttUrl}&lang=${lang.value}`,
        default: false
      }, false);
    });
  }

  // ── Transcript search ──────────────────────────────────────────────────────
  // Searches transcript text nodes for the query, wraps every match in a
  // <mark> span, updates the match count, and wires the up/down arrows to
  // cycle through matches while auto-scrolling each one into view.
  setupSearch() {
    const root        = this.shadowRoot;
    const input       = root.querySelector('#search');
    const searchIcon  = root.querySelector('#icon-search');
    const matchCount  = root.querySelector('#match');
    const arrowDown   = root.querySelector('[data-search="next"]');
    const arrowUp     = root.querySelector('[data-search="prev"]');

    // Internal state
    this._searchMatches      = [];   // Array of <mark> elements for current query
    this._searchCurrentIndex = -1;   // Which match is currently active

    // ── Highlight helpers ──────────────────────────────────────────────────

    // Remove all <mark> elements and restore original text nodes
    const clearHighlights = () => {
      root.querySelectorAll('mark.search-highlight, mark.search-highlight-active').forEach(mark => {
        const parent = mark.parentNode;
        parent.replaceChild(document.createTextNode(mark.textContent), mark);
        parent.normalize();
      });
      this._searchMatches      = [];
      this._searchCurrentIndex = -1;
      matchCount.textContent   = '0';
    };

    // Walk text nodes inside el and wrap query occurrences with <mark>
    const highlightTextNodes = (el, query) => {
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false);
      const nodesToProcess = [];
      let node;
      while ((node = walker.nextNode())) {
        if (node.nodeValue.trim()) nodesToProcess.push(node);
      }

      const re = new RegExp(query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
      nodesToProcess.forEach(textNode => {
        const val = textNode.nodeValue;
        if (!re.test(val)) return;
        re.lastIndex = 0;

        const frag = document.createDocumentFragment();
        let last = 0, m;
        while ((m = re.exec(val)) !== null) {
          if (m.index > last) frag.appendChild(document.createTextNode(val.slice(last, m.index)));
          const mark = document.createElement('mark');
          mark.className   = 'search-highlight';
          mark.textContent = m[0];
          frag.appendChild(mark);
          last = re.lastIndex;
        }
        if (last < val.length) frag.appendChild(document.createTextNode(val.slice(last)));
        textNode.parentNode.replaceChild(frag, textNode);
      });
    };

    // Activate the match at index i: swap CSS class, scroll it into view
    const activateMatch = (i) => {
      if (!this._searchMatches.length) return;

      // Deactivate previous
      if (this._searchCurrentIndex >= 0 && this._searchCurrentIndex < this._searchMatches.length) {
        this._searchMatches[this._searchCurrentIndex].className = 'search-highlight';
      }

      // Clamp and activate new
      this._searchCurrentIndex = (i + this._searchMatches.length) % this._searchMatches.length;
      const el = this._searchMatches[this._searchCurrentIndex];
      el.className = 'search-highlight-active';
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    };

    // ── Main search handler ────────────────────────────────────────────────
    const runSearch = () => {
      clearHighlights();
      const query = input.value.trim();
      if (!query) return;

      const transcriptEl = root.querySelector('#transcript');
      highlightTextNodes(transcriptEl, query);

      this._searchMatches = Array.from(root.querySelectorAll('mark.search-highlight'));
      matchCount.textContent = this._searchMatches.length;

      
      if (this._searchMatches.length > 0) {
        activateMatch(0);
      }
      let st = {};
			st['key'] = query;
			st['value'] = this._searchMatches.length+'';
			this.trackingObj['transcript_search'].push(st);
			this.callAuditAPI("transcript_search");
			this.trackingObj['transcript_search'].pop();
    };

    // ── Event listeners ────────────────────────────────────────────────────

    // Trigger search on Enter key
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') runSearch();
    });

    // Trigger search on icon click
    searchIcon.addEventListener('click', runSearch);

    // Clear highlights when input is fully cleared
    input.addEventListener('input', () => {
      if (!input.value) clearHighlights();
    });

    arrowDown.addEventListener('click', () => {
      if (this._searchMatches.length) activateMatch(this._searchCurrentIndex + 1);
    });

    arrowUp.addEventListener('click', () => {
      if (this._searchMatches.length) activateMatch(this._searchCurrentIndex - 1);
    });

    // Re-run search after transcript is (re)loaded so highlights stay in sync
    this._rerunSearch = () => {
      if (input.value.trim()) runSearch();
    };
  }

  closedCaptionButton() {
    const Button = videojs.getComponent('Button');
    class YouTubeCCButton extends Button {
      constructor(player, options) {
        super(player, options);
        this.controlText('CC');
        this.addClass('vjs-cc-button');

        // YouTube-style icon
        this.el().innerHTML = `
          <span class="vjs-icon-placeholder"></span>
        `;
      }

      handleClick() {
        const tracks = this.player().remoteTextTracks();
        let captionsOn = false;

        // Check whether any caption track is currently showing
        for (let i = 0; i < tracks.length; i++) {
          if (tracks[i].kind === 'captions' && tracks[i].mode === 'showing') {
            captionsOn = true;
            break;
          }
        }

        if (captionsOn) {
          // Turn CC off — disable all caption tracks, but preserve
          // the selected language by setting it to 'hidden' (not 'disabled')
          // so the language picker can find it again on the next CC-on.
          for (let i = 0; i < tracks.length; i++) {
            if (tracks[i].kind === 'captions') {
              tracks[i].mode = tracks[i].mode === 'showing' ? 'hidden' : 'disabled';
            }
          }
        } else {
          // Turn CC on — show the 'hidden' (pre-selected) track, or fall back
          // to the first caption track if none is marked hidden.
          let activated = false;
          for (let i = 0; i < tracks.length; i++) {
            if (tracks[i].kind === 'captions' && tracks[i].mode === 'hidden') {
              tracks[i].mode = 'showing';
              activated = true;
              break;
            }
          }
          // Fallback: no hidden track — show the first caption track
          if (!activated) {
            for (let i = 0; i < tracks.length; i++) {
              if (tracks[i].kind === 'captions') {
                tracks[i].mode = 'showing';
                break;
              }
            }
          }
        }

        this.toggleClass('vjs-cc-on', !captionsOn);
      }
    }
    videojs.registerComponent('YouTubeCCButton', YouTubeCCButton);
  }
  seekButtons() {
    const Button = videojs.getComponent('Button');
    class SeekForwardButton extends Button {
      constructor(player, options) {
        super(player, options);
        this.controlText('Seek Forward 15 seconds');
        this.addClass('vjs-seek-forward');
      }

      handleClick() {
        const currentTime = this.player().currentTime();
        this.player().currentTime(currentTime + 15); // forward 15 sec
      }
    }

    class SeekBackwardButton extends Button {
      constructor(player, options) {
        super(player, options);
        this.controlText('Seek Backward 15 seconds');
        this.addClass('vjs-seek-backward');
      }

      handleClick() {
        const currentTime = this.player().currentTime();
        this.player().currentTime(Math.max(0, currentTime - 15));
      }
    }

    videojs.registerComponent('SeekBackwardButton', SeekBackwardButton);

    videojs.registerComponent('SeekForwardButton', SeekForwardButton);
 
  }
  setupChapters() {
      if (!this.chapterUrl) return;
      const fetchUrl = this.chapterUrl;
      fetch(fetchUrl)
        .then(response => {
          if (!response.ok) throw new Error(`Chapters fetch failed: HTTP ${response.status}`);
          return response.text();
        })
        .then(text => {
          this.loadChapters(text);
          this.shadowRoot.querySelector('#ai-disclaimer-chapters').style.display = 'block';
        })
        .catch(err => {
          console.warn('Chapters not available:', err.message);
          this._showEmptyChapters();
        });
  }
  setupLanguageIcon() {
    const self = this; // capture component instance for use inside inner classes
    const MenuItem = videojs.getComponent('MenuItem');
    const MenuButton = videojs.getComponent('MenuButton');
    const languages = [
      {'label': 'EN US', value: 'en'},
      {'label': 'Chinese (中文)', value: 'zh-CN'},
      {'label': 'Japanese (日本語)', value: 'ja'},
      {'label': 'Portuguese (Português)', value: 'pt-PT'},
      {'label': 'Korean (한국어 )', value: 'ko'},
      {'label': 'Spanish (Español)', value: 'es'},
      {'label': 'French (Français)', value: 'fr'}
		];
  
    class LanguageMenuButton extends MenuButton {
      constructor(player, options) {
        super(player, options);
        this.controlText('Language');
        this.addClass('vjs-language-button');
        const placeholder = this.el().querySelector('.vjs-icon-placeholder');
        if (placeholder) {
          const img = document.createElement('img');
          img.src = iconGlobe;
          img.className = 'vjs-language-icon';
          placeholder.appendChild(img);
        }
      }

      createItems() {
        const languages = [
          {'label': 'EN US', value: 'en'},
          {'label': 'Chinese (中文)', value: 'zh-CN'},
          {'label': 'Japanese (日本語)', value: 'ja'},
          {'label': 'Portuguese (Português)', value: 'pt-PT'},
          {'label': 'Korean (한국어 )', value: 'ko'},
          {'label': 'Spanish (Español)', value: 'es'},
          {'label': 'French (Français)', value: 'fr'}
        ];
        return languages.map(lang => {
          return new LanguageMenuItem(this.player_, {
            label: lang.label,
            value: lang.value,
            selected: this.player_.language().toLocaleLowerCase() === lang.value.toLocaleLowerCase(),
            selectable: true
          });
        });
      }
    }
    videojs.registerComponent('LanguageMenuButton', LanguageMenuButton);

    class LanguageMenuItem extends MenuItem {
      constructor(player, options) {
        super(player, options);
        this.value = options.value;
      }

      handleClick() {
        super.handleClick();
        // Set the player UI locale (video.js control labels)
        this.player_.language(this.value);

        // Switch the active caption track to the selected language.
        // Only swap mode when at least one caption track is currently showing
        // (i.e. CC is on). If CC is off, just record the choice so the next
        // CC-on toggle starts with the right track.
        const tracks = this.player_.remoteTextTracks();
        let ccIsOn = false;
        for (let i = 0; i < tracks.length; i++) {
          if (tracks[i].kind === 'captions' && tracks[i].mode === 'showing') {
            ccIsOn = true;
            break;
          }
        }
        for (let i = 0; i < tracks.length; i++) {
          const track = tracks[i];
          if (track.kind !== 'captions') continue;
          const isSelected = track.language.toLowerCase() === this.value.toLowerCase();
          if (ccIsOn) {
            track.mode = isSelected ? 'showing' : 'disabled';
          } else {
            // CC is off — mark the chosen track so it activates when CC is turned on
            track.mode = isSelected ? 'hidden' : 'disabled';
          }
        }

        // Rebuild menu to reflect updated selected state
        this.player_.getChild('controlBar')
          .getChild('LanguageMenuButton')
          .update();

        // Re-fetch localised transcript, chapters and summary.
        const shortLang  = this.value.split('-')[0];
        const sourceLang = self.video_source_language || 'en';

        // Clear panels immediately so stale content from the previous language
        self.shadowRoot.querySelector('#chapters').innerHTML = '';
        self.shadowRoot.querySelector('#summaryDiv').innerHTML = '';
        self.shadowRoot.querySelector('#transcript').innerHTML = '';

        self.lang_change_transcript(shortLang);

        if (shortLang === sourceLang) {
          self.getChapters(self.assetId);
          self.getSummary(self.assetId);
        } else {
          self.getChapters(self.assetId + '&lang=' + shortLang);
          self.getSummary(self.assetId + '&lang=' + shortLang);
        }
      }
    }

    videojs.registerComponent('LanguageMenuButton', LanguageMenuButton);
  }
  setupSummary() {
    if (!this.summaryUrl) return;
    const fetchUrl = this.summaryUrl;
    fetch(fetchUrl)
      .then(response => {
        if (!response.ok) throw new Error(`Summary fetch failed: HTTP ${response.status}`);
        return response.text();
      })
      .then(text => {
        if (text) {
          this.loadSummary(text);
          this.shadowRoot.querySelector('#ai-disclaimer-summary').style.display = 'block';
        } else {
          this._showEmptySummary();
        }
      })
      .catch(err => {
        console.warn('Summary not available:', err.message);
        this._showEmptySummary();
      });
  }
  getAssetDetails() {

    const url = this.config.restapi_base_url
              + `/cdamplayer/v1/assetdetails/${this.assetId}`;
    fetch(url)
      .then(response => {
        if (!response.ok) throw new Error(`Asset details fetch failed: HTTP ${response.status}`); 
        return response.json();
      })
      .then(data => {
        this.video_source_language = data.language ? data.language.toLowerCase() : '';
        const shortCode = data.language ? data.language.toLowerCase() : 'en';
        this.video_source_language = shortCode;
        const match = this.languages.find(l => l.value.toLowerCase().startsWith(shortCode));
        const locale = match ? match.value : shortCode;
        if (this.player) {
          this.player.language(locale);
          // Rebuild the language menu so the checkmark reflects the source language.
          const langMenuBtn = this.player.controlBar?.getChild('LanguageMenuButton');
          if (langMenuBtn) langMenuBtn.update();
        }
        this.loadTextTracks();
        // Capture overlay links and initialise the overlay plugin
        this.overlayLinks = (data.overlays && Array.isArray(data.overlays)) ? data.overlays : [];
        this.setupOverlays();
      })
      .catch(err => {
        console.warn('Could not fetch asset details, defaulting to English tracks:', err.message);
        this.video_source_language = 'en';
        this.loadTextTracks();
      });
  }

  // Converts "HH:MM:SS" or "MM:SS" timestamp string to total seconds (number).
  _convertToSeconds(str) {
    const parts = str.split(':');
    let seconds = 0, multiplier = 1;
    while (parts.length > 0) {
      seconds += multiplier * parseInt(parts.pop(), 10);
      multiplier *= 60;
    }
    return seconds;
  }

  // Ensures the URL has a protocol prefix
  _isHttpValidURL(str) {
    if (str.indexOf('http://') === -1 && str.indexOf('https://') === -1) {
      str = 'https://' + str;
    }
    return str;
  }

  // Transforms raw API overlay objects into the format expected by videojs-overlay
  setupOverlays() {
    if (!this.player || !this.overlayLinks || this.overlayLinks.length === 0) return;

    const overlays = this.overlayLinks.map(ov => {
      const startSecs = this._convertToSeconds(ov.overlay_start_time);
      const url       = this._isHttpValidURL(ov.overlay_url);
      const isDark    = ov.overlay_background.toLowerCase() === 'dark';
      const infoIcon  = isDark ? iconInfoLight : iconInfoDark;

      return {
        content: `<a class="cdam-overlay-link" href="${url}" target="_blank" rel="noopener noreferrer">` +
                   `<span class="overlay-info-img" style="background-image: url(${infoIcon})"></span>` +
                   `<span class="overlay-text-span">${ov.overlay_title}</span>` +
                 `</a>`,
        start:   startSecs,
        end:     startSecs + 10,  // each overlay is shown for exactly 10 seconds
        align:   'top-right',
        class:   'overlay-' + ov.overlay_background.toLowerCase()
      };
    });

    this.player.overlay({
      content:  '',
      overlays: overlays
    });

    // Hide any visible overlays when the video ends.
    if (!this._overlayEndedListenerAdded) {
      this._overlayEndedListenerAdded = true;
      this.player.on('ended', () => {
        this.shadowRoot.querySelectorAll('.vjs-overlay:not(.vjs-hidden)').forEach(el => {
          el.classList.add('vjs-hidden');
        });
      });
    }

    // Event delegation: handle overlay link clicks inside the Shadow DOM.
    if (!this._overlayClickDelegated) {
      this._overlayClickDelegated = true;
      this.shadowRoot.addEventListener('click', e => {
        const link = e.target.closest('.cdam-overlay-link');
        if (!link) return;

        this.player.pause();
        this.trackingObj['overlay_clicked_time'] = this.player.currentTime();
        this.trackingObj['overlay_title']        = link.querySelector('.overlay-text-span')?.textContent || '';
        this.trackingObj['overlay_url']          = link.href;
        this.callAuditAPI('overlay_click');
        // Clean up overlay-specific fields so they don't appear in unrelated audit calls
        delete this.trackingObj['overlay_clicked_time'];
        delete this.trackingObj['overlay_title'];
        delete this.trackingObj['overlay_url'];
      });
    }
  }

  lang_change_transcript(lang) {
    const sourceLang = this.video_source_language || 'en';
    const param      = lang !== sourceLang ? '&lang=' + lang : '';
    const fetchUrl   = this.vttUrl + param;
    fetch(fetchUrl)
      .then(response => {
        if (!response.ok) throw new Error(`VTT fetch failed: HTTP ${response.status}`);
        return response.text();
      })
      .then(text => {
        this.loadCaptions(text);
      })
      .catch(err => {
        console.warn('Transcript lang fetch failed:', err.message);
        this._showEmptyTranscript();
      });
  }

  getChapters(queryParam) {
    const fetchUrl = this.config.chapter_url + '/chapters?id=' + queryParam;
    fetch(fetchUrl)
      .then(response => {
        if (!response.ok) throw new Error(`Chapters fetch failed: HTTP ${response.status}`);
        return response.text();
      })
      .then(text => {
        this.loadChapters(text);
      })
      .catch(err => {
        console.warn('Chapters lang fetch failed:', err.message);
        this._showEmptyChapters();
      });
  }

  getSummary(queryParam) {
    const fetchUrl = this.config.chapter_url + '/summary?id=' + queryParam;
    fetch(fetchUrl)
      .then(response => {
        if (!response.ok) throw new Error(`Summary fetch failed: HTTP ${response.status}`);
        return response.text();
      })
      .then(text => {
        if (text) {
          this.loadSummary(text);
        } else {
          this._showEmptySummary();
        }
      })
      .catch(err => {
        console.warn('Summary lang fetch failed:', err.message);
        this._showEmptySummary();
      });
  }

  // Parses raw VTT text and renders caption items into #transcript.
  loadCaptions(text) {
    const transcriptEl = this.shadowRoot.querySelector('#transcript');
    this.captions = [];

    const lines = text.split('\n');
    let html      = '<p>';
    let s         = 0;
    let printLine = false;

    for (let i = 0; i < lines.length; i++) {
      if (lines[i].indexOf('-->') > -1) {
        printLine = true;
        continue;
      }
      if (printLine) {
        printLine = false;
        const ii      = lines[i - 1].indexOf(' --> ');
        const newText = lines[i]
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
        const c = {
          begin: this._secondsFromVtt(lines[i - 1].substr(0, ii)),
          btext: this._transcriptTimestamp(lines[i - 1], ii),
          end:   this._secondsFromVtt(lines[i - 1].substr(ii + 5)),
          text:  newText
        };
        html += `<hr class="chapter-line">` +
                `<span class="caption-item" id="caption${s}" data-time="${c.begin}">` +
                  `<span class="timestamp-wrap">` +
                    `<span class="chapter-text trans-content">${newText}</span>` +
                    `<span>${c.btext}</span>` +
                  `</span>` +
                `</span>`;
        this.captions.push(c);
        s++;
      }
    }

    transcriptEl.innerHTML = html + '</p>';

    // Click on a caption item → seek video to that timestamp
    transcriptEl.querySelectorAll('.caption-item').forEach(el => {
      el.addEventListener('click', () => {
        this.player.currentTime(parseFloat(el.dataset.time));
      });
    });

    // Re-apply any active search query after transcript DOM is rebuilt
    if (this._rerunSearch) this._rerunSearch();
  }

  loadChapters(text) {
    this.chapters = [];
    let t = text.split("\n");
    let h = "<p>";
    let s = 0;
    let printLine = false;

    for (let i = 0; i < t.length; i++) {
      if (printLine) {
        printLine = false;
        const ii = t[i - 1].indexOf(' --> ');
        const newText = t[i]
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
        const c = {
          begin: this._secondsFromVtt(t[i - 1].substr(0, ii)),
          btext: this._transcriptTimestamp(t[i - 1], ii),
          end: this._secondsFromVtt(t[i - 1].substr(ii + 5)),
          text: newText
        };
        h += `<hr class="chapter-line">
          <span class="caption-item" id="chapter${s}" data-time="${c.begin}">
            <span class="timestamp-wrap">
              <span class="chapter-text">${newText}</span>
              <span class="">${c.btext}</span>
            </span>
          </span>`;
        this.chapters.push(c);
        s++;
      }
      if (t[i].indexOf('-->') > -1) {
        printLine = true;
        continue;
      }
    }
    var chaptersEL = this.shadowRoot.querySelector('#chapters');
    chaptersEL.innerHTML = h + "</p>";
    chaptersEL.querySelectorAll('.caption-item').forEach(el => {
      el.addEventListener('click', () => {
        this.player.currentTime(parseFloat(el.dataset.time));
        // Read chapter title from DOM
        const textValue = el.querySelector('.chapter-text').innerHTML;
        const chapter_payload = {
          userid:          this.userId,
          assetid:         this.assetId,
          chapter_clicked: true,
          chapter_title:   textValue,
          chapter_time:    parseFloat(el.dataset.time),
          createdDate:     getTimestamp() + '.000Z',
          experience_id:   this.expParam,
          session_type:    this.sessionType
        };
        this.saveChapters(chapter_payload);
      });
    });
  }

  loadSummary(text) {
    const summaryDiv = this.shadowRoot.querySelector('#summaryDiv');
    const trimmed = text.trim();

    if (/^<!DOCTYPE/i.test(trimmed) || /<head[\s>]/i.test(trimmed)) {
      this._showEmptySummary();
      return;
    }

    const start = trimmed.indexOf('<');
    const end   = trimmed.lastIndexOf('>');

    if (start === -1 || end === -1) {
      summaryDiv.innerHTML = trimmed; // plain text response
    } else {
      summaryDiv.innerHTML = trimmed.substring(start, end + 1); // HTML response
    }
  }

  setupFeedbackButton() {
    const showButtons = this.expParam === 'guided-resources' || this.expParam === 'post';
    if (showButtons) {
      this.shadowRoot.querySelector('.video-details').style.display = 'block';
      this.shadowRoot.querySelector('#like-dislike-buttons').style.display = 'block';
    }
    const likeBtn    = this.shadowRoot.querySelector('#likevideo_id');
    const dislikeBtn = this.shadowRoot.querySelector('#dislikevideo_id');

    likeBtn.addEventListener('click', () => {
      const likeImg    = this.shadowRoot.querySelector('#video-like-span');
      const dislikeImg = this.shadowRoot.querySelector('#video-dislike-span');
      // Reset dislike icon
      dislikeImg.classList.remove('video-disliked-img');
      dislikeImg.classList.add('video-dislike-img');
      dislikeImg.style.backgroundImage = `url(${iconDislike})`;
      // Toggle like
      if (likeImg.classList.contains('video-liked-img')) {
        this.feedbackObj._liked = null;
        likeImg.classList.remove('video-liked-img');
        likeImg.classList.add('video-like-img');
        likeImg.style.backgroundImage = `url(${iconLike})`;
      } else {
        this.feedbackObj._liked = true;
        likeImg.classList.remove('video-like-img');
        likeImg.classList.add('video-liked-img');
        likeImg.style.backgroundImage = `url(${iconLiked})`;
      }
      this.saveFeedback(true);
    });

    dislikeBtn.addEventListener('click', () => {
      const dislikeImg = this.shadowRoot.querySelector('#video-dislike-span');
      const likeImg    = this.shadowRoot.querySelector('#video-like-span');
      // Reset like icon
      likeImg.classList.remove('video-liked-img');
      likeImg.classList.add('video-like-img');
      likeImg.style.backgroundImage = `url(${iconLike})`;
      // Toggle dislike
      if (dislikeImg.classList.contains('video-disliked-img')) {
        this.feedbackObj._liked = null;
        dislikeImg.classList.remove('video-disliked-img');
        dislikeImg.classList.add('video-dislike-img');
        dislikeImg.style.backgroundImage = `url(${iconDislike})`;
      } else {
        this.feedbackObj._liked = false;
        dislikeImg.classList.remove('video-dislike-img');
        dislikeImg.classList.add('video-disliked-img');
        dislikeImg.style.backgroundImage = `url(${iconDisliked})`;
      }
      this.saveFeedback(false);
    });
  }

  getFeedback() {
    const url = this.config.restapi_base_url
              + `/cdamplayer/v1/public/feedback/${this.userId}/${this.assetId}`;
    fetch(url)
      .then(response => {
        if (!response.ok) throw new Error(`getFeedback HTTP ${response.status}`);
        return response.json();
      })
      .then(data => {
        if (data && data['userid']) {
          this.feedbackObj._liked = data['_liked'];
          const likeImg    = this.shadowRoot.querySelector('#video-like-span');
          const dislikeImg = this.shadowRoot.querySelector('#video-dislike-span');
          if (this.feedbackObj._liked === true) {
            likeImg.classList.remove('video-like-img');
            likeImg.classList.add('video-liked-img');
            likeImg.style.backgroundImage = `url(${iconLiked})`;
          } else if (this.feedbackObj._liked === false) {
            dislikeImg.classList.remove('video-dislike-img');
            dislikeImg.classList.add('video-disliked-img');
            dislikeImg.style.backgroundImage = `url(${iconDisliked})`;
          }
        }
      })
      .catch(err => console.warn('Failed to fetch feedback:', err.message));
  }

  saveFeedback(isLiked) {
    if (!this.userId) return; // skip silently if no user-id attribute
    const fbObj = {
      userid:       this.userId,
      assetid:      this.assetId,
      _liked:       isLiked,
      time:         this.player.currentTime(),
      created_date: getTimestamp() + '.000Z',
      experience_id: this.expParam,
      client_id:    this.clientParam
    };
    const url = this.config.restapi_base_url + '/cdamplayer/v1/public/feedback';
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fbObj)
    })
    .then(response => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    })
    .then(result => console.log('Feedback saved successfully'))
    .catch(err  => console.error('Failed to save feedback:', err.message));
  }

  saveChapters(chapter_payload) {
    const url = this.config.restapi_base_url + '/cdamplayer/v1/chapters';
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(chapter_payload)
    })
    .then(response => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    })
    .then(() => console.log('Chapter saved successfully'))
    .catch(err => console.error('Failed to save chapter info:', err.message));
  }

  saveSummaryData() {
    const summary_payload = {
      userid:             this.userId,
      assetid:            this.assetId,
      summary_clicked:    true,
      summary_click_time: this.player.currentTime(),
      createdDate:        getTimestamp() + '.000Z',
      experience_id:      this.expParam,
      session_type:       this.sessionType
    };
    const url = this.config.restapi_base_url + '/cdamplayer/v1/summary';
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(summary_payload)
    })
    .then(response => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    })
    .then(() => console.log('Summary saved successfully'))
    .catch(err => console.error('Failed to save summary info:', err.message));
  }

  downloadTranscript() {
      const downloadTranscriptBtn = this.shadowRoot.querySelector('#downloadTranscriptBtn');
      downloadTranscriptBtn.addEventListener('click', () => {
        const transcriptUrl = this.vttUrl; // Assuming the servlet URL is the same as the VTT URL
        if (null != transcriptUrl) {
          this.trackingObj['transcript_downloaded'] = true;
          this.callAuditAPI("transcript_downloaded");
          window.open(transcriptUrl, '_blank'); //Open servlet which downloads VTT file with title as fileName
        }
      });
  }
  _showEmptySummary() {
    const key = 'Summary is currently being generated. It will be available shortly.';
    const summaryDiv = this.shadowRoot.querySelector('#summaryDiv');
    summaryDiv.innerHTML = `<p class='no-chapters' data-i18n="${key}">${key}</p>`;
    this.applyCustomTranslations();
  }

  // Shown when VTT fetch fails or returns non-200.
  _showEmptyTranscript() {
    const key = 'Transcript is currently being generated. They will be available shortly.';
    const transcriptEl = this.shadowRoot.querySelector('#transcript');
    transcriptEl.innerHTML = `<p id='no-transcript' class='no-chapters' data-i18n="${key}">${key}</p>`;
    this.applyCustomTranslations();
  }

  _showEmptyChapters() {
    const key = 'Chapters are currently being generated. They will be available shortly.';
    const chaptersEl = this.shadowRoot.querySelector('#chapters');
    chaptersEl.innerHTML = `<p class='no-chapters' data-i18n="${key}">${key}</p>`;
    this.applyCustomTranslations();
  }

  // Converts a VTT timestamp string ("00:01:23.456") to seconds (float).
  _secondsFromVtt(s) {
    const a = s.trim().split(':');
    let r = parseFloat(a[a.length - 1]) + parseInt(a[a.length - 2], 10) * 60;
    if (a.length > 2) r += parseInt(a[a.length - 3], 10) * 3600;
    return r;
  }

  // Returns a short display timestamp (strips leading "00:" for sub-hour videos).
  _transcriptTimestamp(timeStamp, ii) {
    const full  = timeStamp.substr(0, ii - 4);
    const parts = full.split(':');
    // If hours part is '00', drop it (e.g. "00:01:23" → "01:23")
    return parts[0] !== '00' ? full : timeStamp.substr(3, ii - 7);
  }

  // Sets video source and optionally seeks to a resume position
  loadPlayer(startTime) {
    if (!this.videoUrl) return;
    if (!this.player.src()) {
      this.player.src({ src: this.videoUrl, type: 'video/mp4' });
    }
    if (startTime) {
      // Wait until metadata is loaded before seeking
      this.player.one('loadedmetadata', () => {
        this.player.currentTime(parseFloat(startTime));
      });
    }
  }

  async initSSO() {
    // Try to get SSO token from WEM host page
    const token = await window.cdc?.sso?.hasAuthHeader?.() ?? false;

    if (token) {
      this.accessToken     = token;
      this.isAuthenticated = !!this.userId;
      console.log("token fetched");
      if (this.isAuthenticated) {
        await this.init(true);
        return;
      }
    }

    // Not authenticated
    this.isAuthenticated = false;
    await this.init(false);
  }

  async init(isAuthenticated) {
    // Call Progress and Feedback API whenever userId is present, regardless of SSO token
    if (this.userId) {
      await this.getProgress();
    }
    if (this.userId && (this.expParam === 'guided-resources' || this.expParam === 'post')) {
      this.getFeedback();
    }
    if (isAuthenticated) {
      this.buildAuditObject();
    } else {
      // Video already loading from beginning, build the audit object
      setTimeout(() => this.buildAuditObject(), 500);
    }
  }

  async getProgress() {
    // Public endpoint — no Authorization header needed
    const url = this.config.restapi_base_url
              + `/cdamplayer/v1/public/progress/${this.userId}/${this.assetId}`;
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`getProgress HTTP ${response.status}`);
      const data = await response.json();
      if (data && data.time) {
        // Resume time found — show confirmation dialog
        this._showResumeDialog(data.time);
      }
    } catch (err) {
      console.warn('Failed to fetch progress, loading from beginning:', err);
    }
  }

  _showResumeDialog(resumeTime) {
    const dialog   = this.shadowRoot.querySelector('#resume-dialog');
    const backdrop = this.shadowRoot.querySelector('.resume-backdrop');
    const yesBtn   = this.shadowRoot.querySelector('#resume-yes');
    const noBtn    = this.shadowRoot.querySelector('#resume-no');

    // positioned absolutely over the player via CSS on .container.
    backdrop.classList.add('active');
    dialog.show();

    const closeDialog = () => {
      dialog.close();
      backdrop.classList.remove('active');
    };

    yesBtn.onclick = () => {
      closeDialog();
      // Seek to resume position
      if (this.player.readyState() >= 1) {
        this.player.currentTime(parseFloat(resumeTime));
      } else {
        this.player.one('loadedmetadata', () => this.player.currentTime(parseFloat(resumeTime)));
      }
      this.player.play();
      this.deleteUserProgress();
      this.trackingObj['progress'] = {
        time:    secondsTimeSpanToHMS(resumeTime).substr(2, 5),
        resumed: 'yes'
      };
      this.callAuditAPI('resume');
    };

    noBtn.onclick = () => {
      closeDialog();
      this.player.play();
      this.deleteUserProgress();
      this.trackingObj['progress'] = {
        time:    secondsTimeSpanToHMS(resumeTime).substr(2, 5),
        resumed: 'no'
      };
      this.callAuditAPI('resume');
    };
  }

  deleteUserProgress() {
    if (!this.userId) return;
    const url = this.config.restapi_base_url
              + `/cdamplayer/v1/public/progress/${this.userId}/${this.assetId}`;
    fetch(url, { method: 'DELETE' })
      .then(response => { if (!response.ok) throw new Error(`HTTP ${response.status}`); })
      .then(() => console.log('Progress deleted'))
      .catch(err => console.warn('Failed to delete progress:', err.message));
  }

  saveUserProgress() {
    if (!this.userId) {
      console.log('No user-id attribute, skipping progress save');
      return;
    }

    const data = {
      userid: this.userId,
      assetid: this.assetId,
      createdDate: getTimestamp() + '.000Z',
      time: this.player.currentTime(),
      experience_id: this.expParam,
      client_id: this.clientParam
    };

    // Public endpoint — no Authorization header needed
    const apiUrl = this.config.restapi_base_url + '/cdamplayer/v1/public/progress';

    fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })
    .then(response => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    })
    .then(result => {
      console.log('Save progress successful');
    })
    .catch(error => {
      console.error('Failed to save user progress:', error.message);
    });
  }

  buildAuditObject() {
    this.trackingObj['transcript_downloaded'] = false;
    this.trackingObj['content_downloaded']    = false;
    this.trackingObj['content_ended']         = false;
    this.trackingObj['content_seeked']        = false;
    this.trackingObj['seek_times']            = [];
    this.trackingObj['content_id']            = this.assetId;
    this.trackingObj['created_date']          = getTimestamp() + '.000Z';
    this.trackingObj['userid']                = this.userId;
    this.trackingObj['client_id']             = this.clientParam;
    this.trackingObj['experience_id']         = this.expParam;
    this.trackingObj['transcript_search']     = [];
    this.trackingObj['device']                = deviceType();
    this.trackingObj['browser']               = browserType();
    this.trackingObj['tenant']                = this.tenant;
    this.trackingObj['location']              = Intl.DateTimeFormat().resolvedOptions().timeZone;
    this.trackingObj['new_request']           = true;
    this.trackingObj['session_type']          = this.sessionType;
    this.trackingObj['source']                = window.location.href;
  }

  setupAuditTracking() {
    if (!this.player) return;

    this.buildAuditObject();

    this.player.on('play', () => {
      this.trackingObj['video_length'] = this.player.duration();

      if (this.firstPlay) {
        this.trackingObj['viewed'] = true;
        this.callAuditAPI('play - first view');
        delete this.trackingObj['viewed'];  // clean up after sending
        this.firstPlay = false;
      }

      // Save progress if resuming after seeking while paused
      if (this.pauseSeeking) {
        if (this.player.remainingTime() != 0) {
          this.saveUserProgress();
        }
        this.pauseSeeking = false;
      }
      this.playcount++;
    });

    this.player.on('pause', () => {
      // Save progress only if not at the end of video
      if (this.player.remainingTime() != 0) {
        this.saveUserProgress();
      }

      const p       = {};
      p['resumed']  = 'no';
      p['time']     = this.player.currentTime().toString();
      this.trackingObj['progress'] = p;
      this.callAuditAPI('pause');
    });

    this.player.on('seeked', () => {
      this.trackingObj['content_seeked'] = true;
      this.trackingObj['seek_times'].push(
        secondsTimeSpanToHMS(this.player.currentTime()).substr(2, 5)  // e.g. "02:35"
      );
      this.callAuditAPI('seek');
      this.trackingObj['content_seeked'] = false;  // reset after sending

      // Set flag if seeking while paused (will save progress on next play)
      if (this.player.paused()) {
        this.pauseSeeking = true;
      }

      // Save progress if user seeks while playing (after first play)
      if (this.playcount > 1 && !this.pauseSeeking) {
        if (this.player.remainingTime() != 0) {
          this.saveUserProgress();
        }
      }
    });

    this.player.on('ended', () => {
      this.trackingObj['content_ended'] = true;
      this.callAuditAPI('ended');
      this.deleteUserProgress();
    });

    // Highlight the active transcript line and active chapter as the video plays
    this.player.on('timeupdate', () => {
      const p = this.player.currentTime();

      // Transcript highlight
      if (this.captions.length) {
        const transcriptEl = this.shadowRoot.querySelector('#transcript');
        for (let j = 0; j < this.captions.length; j++) {
          if (this.captions[j].begin < p && this.captions[j].end > p) {
            if (j !== this.captionIndex) {
              // Remove highlight from previous active caption
              if (this.captionIndex > -1) {
                const prev = this.shadowRoot.querySelector('#caption' + this.captionIndex);
                if (prev) prev.classList.remove('active-chapter');
              }
              // Highlight new active caption and auto-scroll into view
              const curr = this.shadowRoot.querySelector('#caption' + j);
              if (curr) {
                curr.classList.add('active-chapter');
                transcriptEl.scrollTop = curr.offsetTop - 40;
              }
              this.captionIndex = j;
            }
            break;
          }
        }
      }

      // Chapters highlight
      if (this.chapters.length) {
        const chaptersEl = this.shadowRoot.querySelector('#chapters');
        for (let j = 0; j < this.chapters.length; j++) {
          if (this.chapters[j].begin < p && this.chapters[j].end > p) {
            if (j !== this.chapterIndex) {
              // Remove highlight from previous active chapter
              if (this.chapterIndex > -1) {
                const prev = this.shadowRoot.querySelector('#chapter' + this.chapterIndex);
                if (prev) prev.classList.remove('active-chapter');
              }
              // Highlight new active chapter and auto-scroll into view
              const curr = this.shadowRoot.querySelector('#chapter' + j);
              if (curr) {
                curr.classList.add('active-chapter');
                chaptersEl.scrollTop = curr.offsetTop - 40;
              }
              this.chapterIndex = j;
            }
            break;
          }
        }
      }
    });
  }

  callAuditAPI(eventType) {
    // restapi_base_url is '' on dev/stage (relative path, same host)
    // and an explicit origin on prod (bypasses CDN)
    const apiUrl  = this.config.restapi_base_url + '/cdamplayer/v1/audit';
    const headers = { 'Content-Type': 'application/json' };

    // Include Authorization header on authenticated calls
    if (this.accessToken) {
      headers['Authorization'] = this.accessToken;
    }

    fetch(apiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(this.trackingObj)
    })
    .then(response => {
      const contentType = response.headers.get('content-type') || '';
      if (!response.ok) {
        // Server returned 4xx/5xx — read as text to see the actual error message
        return response.text().then(text => {
          throw new Error(`HTTP ${response.status}: ${text}`);
        });
      }
      // Only parse as JSON if the response actually is JSON
      if (contentType.includes('application/json')) {
        return response.json();
      }
      return response.text();
    })
    .then(result => {
      console.log(`Audit [${eventType}] successful`);
    })
    .catch(error => {
      console.error(`Audit [${eventType}] error:`, error.message);
    });
  }

  // Cleanup when component is removed
  disconnectedCallback() {
    if (this.player) {
      this.player.dispose();
    }
  }

//   attributeChangedCallback(name, oldValue, newValue) { // runs when name attribute changes
//     if (name === 'name' && oldValue !== newValue) {
//       this.render();
//     }
//   }

  render() {
    // Video player is already rendered and initialized
    // This method can be used for future dynamic updates
    // For example: updating video source, poster, captions based on attributes
  }
}

customElements.define('cdam-video-player', CdamVideoPlayer);